import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { IntakeData } from "@/lib/types";
import { SpecialtySelect } from "@/components/form/SpecialtySelect";
 
import {
  FileText,
  Loader2,

} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Trans,  msg,} from "@lingui/macro";
import { useLingui } from "@lingui/react";

const CONDITION_OPTIONS = [
  { value: "none", label: msg`None`, rawLabel: "None" },
  { value: "diabetes", label: msg`Diabetes`, rawLabel: "Diabetes" },
  { value: "hypertension", label: msg`Hypertension`, rawLabel: "Hypertension" },
  { value: "heart_disease", label: msg`Heart Disease`, rawLabel: "Heart Disease" },
  { value: "asthma", label: msg`Asthma`, rawLabel: "Asthma" },
  { value: "arthritis", label: msg`Arthritis`, rawLabel: "Arthritis" },
  { value: "depression", label: msg`Depression/Anxiety`, rawLabel: "Depression/Anxiety" },
  { value: "thyroid", label: msg`Thyroid Disorder`, rawLabel: "Thyroid Disorder" },
  { value: "other", label: msg`Other`, rawLabel: "Other" },
] as const;

 
const SMOKING_OPTIONS = [
  { value: "no", label: msg`I do not smoke` },
  { value: "yes_1_5", label: msg`Yes, 1-5 cigarettes/day` },
  { value: "yes_5_10", label: msg`Yes, 5-10 cigarettes/day` },
  { value: "yes_over_10", label: msg`Yes, over 10/day` },
] as const;

const ALCOHOL_OPTIONS = [
  { value: "no", label: msg`No` },
  { value: "occasionally", label: msg`Occasionally` },
  { value: "regularly", label: msg`Regularly` },
  { value: "daily", label: msg`Daily` },
] as const;

const EXERCISE_OPTIONS = [
  { value: "none", label: msg`None` },
  { value: "1_2_week", label: msg`1-2 times/week` },
  { value: "3_5_week", label: msg`3-5 times/week` },
  { value: "daily", label: msg`Daily` },
] as const;


const formatPreExistingConditionSummary = (
  conditions: unknown,
  otherConditionNote: string,
  conditionLabels: Record<string, string>,
): string => {
  if (!Array.isArray(conditions) || conditions.length === 0) {
    return "";
  }

  if (conditions.includes("none")) {
    return "None";
  }

  const uniqueConditions = Array.from(
    new Set(
      (conditions as string[]).filter(
        (condition) => condition && condition !== "none",
      ),
    ),
  );

  const formatted = uniqueConditions
    .map((condition) => {
      if (condition === "other") {
        return otherConditionNote
          ? `Other: ${otherConditionNote}`
          : conditionLabels[condition] ?? "Other";
      }
      return conditionLabels[condition] ?? condition.replace(/_/g, " ");
    })
    .filter(Boolean);

  if (otherConditionNote && !uniqueConditions.includes("other")) {
    formatted.push(`Other: ${otherConditionNote}`);
  }

  return formatted.join(", ");
};

const parsePreExistingConditions = (
  preExisting?: string,
): string[] => {
  if (!preExisting) {
    return [];
  }

  const normalized = preExisting.toLowerCase();

  if (normalized.includes("none")) {
    return ["none"];
  }

  const selections = CONDITION_OPTIONS.filter((option) => {
    if (option.value === "none") {
      return false;
    }
    if (option.value === "other") {
      return normalized.includes("other");
    }
    return normalized.includes(option.rawLabel.toLowerCase());
  }).map((option) => option.value);

  return Array.from(new Set(selections));
};

const extractOtherConditionNote = (preExisting?: string): string => {
  if (!preExisting) {
    return "";
  }

  const match = preExisting.match(/other[:-]?\s*(.*)$/i);
  return match ? match[1].trim() : "";
};

const mergeAllergyMedicationNotes = (
  allergiesMedications?: string,
  allergies?: string,
  medications?: string,
) => {
  if (allergiesMedications && allergiesMedications.trim().length > 0) {
    return allergiesMedications;
  }
  if (allergies && medications && allergies.trim() && medications.trim()) {
    if (allergies.trim() === medications.trim()) {
      return allergies.trim();
    }
    return `Allergies: ${allergies.trim()}\nMedications: ${medications.trim()}`;
  }
  if (allergies && allergies.trim().length > 0) {
    return allergies.trim();
  }
  if (medications && medications.trim().length > 0) {
    return medications.trim();
  }
  return "";
};

interface MedicalProcedureStepProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function MedicalProcedureStep({
  data,
  onUpdate,
  onNext,
  onBack,
}: MedicalProcedureStepProps) {
  const { i18n } = useLingui();

  const [isDentistry, setIsDentistry] = useState(false); // bool state


  const conditionLabels = useMemo(
    () =>
      CONDITION_OPTIONS.reduce<Record<string, string>>((acc, option) => {
        acc[option.value] = i18n._(option.label);
        return acc;
      }, {}),
    [i18n],
  );
  const translatedConditionOptions = useMemo(
    () =>
      CONDITION_OPTIONS.map((option) => ({
        ...option,
        translatedLabel: i18n._(option.label),
      })),
    [i18n],
  );

  const [formData, setFormData] = useState(() => {
    const existingConditions =
      Array.isArray(data.currentMedicalConditions) &&
        data.currentMedicalConditions.length > 0
        ? data.currentMedicalConditions
        : parsePreExistingConditions(data.preExistingConditions);

    const mergedAllergyMedicationNotes = mergeAllergyMedicationNotes(
      data.allergiesMedications,
      data.allergies,
      data.currentMedications,
    );
 
    // Parse height if it exists (format: "3ft 5in" or similar)
    const parseHeight = (heightStr?: string) => {
      if (!heightStr) return { feet: "", inches: "" };
      const feetMatch = heightStr.match(/(\d+)\s*ft/i);
      const inchesMatch = heightStr.match(/(\d+)\s*in/i);
      return {
        feet: feetMatch ? feetMatch[1] : "",
        inches: inchesMatch ? inchesMatch[1] : "",
      };
    };
    const parsedHeight = parseHeight(data.height);

    return {
      procedures: typeof data.procedures === 'string' ? data.procedures : '',
      dateOfBirth: data.dateOfBirth || "",
      height: data.height || "", 
      heightFeet: parsedHeight.feet,
      heightInches: parsedHeight.inches,
      weight: data.weight || "",
      currentMedicalConditions: existingConditions,
      otherCondition:
        data.otherCondition || extractOtherConditionNote(data.preExistingConditions),
      pastSurgeries: data.pastSurgeries || data.majorSurgeries || "",
      allergiesMedications: mergedAllergyMedicationNotes,
      smokingHabits: data.smokingHabits || "",
      alcoholUse: data.alcoholUse || data.alcoholConsumption || "",
      exerciseFrequency: data.exerciseFrequency || "",
      dentalProcedureType: data.dentalProcedureType || "",
      dentalDiscomfort: data.dentalDiscomfort || "",
      mobilityIssues: data.mobilityIssues, 
      dietaryRestrictions: data.dietaryRestrictions,
    };
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate height in cm
  const heightInCm = useMemo(() => {
    const feet = parseFloat(formData.heightFeet) || 0;
    const inches = parseFloat(formData.heightInches) || 0;
    if (feet === 0 && inches === 0) return null;
    const totalInches = feet * 12 + inches;
    return (totalInches * 2.54).toFixed(1);
  }, [formData.heightFeet, formData.heightInches]);

  // Calculate weight in kg (from pounds input)
  const weightInKg = useMemo(() => {
    const lbs = parseFloat(formData.weight);
    if (!lbs || isNaN(lbs)) return null;
    return (lbs / 2.20462).toFixed(1);
  }, [formData.weight]);

  const handleInputChange = (field: string, value: string | string[]) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      if (field === "procedures"  ) {
        if(typeof value === "string" && value === "dentistry"){
          setIsDentistry(true); 
        }else{
          setIsDentistry(false); 
        } 
      }

      if (field === "heightFeet" || field === "heightInches") {
        const feet = field === "heightFeet" ? value as string : prev.heightFeet || "";
        const inches = field === "heightInches" ? value as string : prev.heightInches || "";
        if (feet || inches) {
          updated.height = `${feet}ft ${inches}in`.trim();
        } else {
          updated.height = "";
        }
      }

      return updated;
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
    // Clear height error if either feet or inches changes
    if ((field === "heightFeet" || field === "heightInches") && errors.height) {
      setErrors((prev) => ({ ...prev, height: "" }));
    }
  };

  const handleConditionChange = (condition: string, checked: boolean) => {
    const currentConditions = Array.isArray(formData.currentMedicalConditions)
      ? formData.currentMedicalConditions
      : [];

    if (checked) {
      let newConditions = [...currentConditions, condition];

      // If "none" is selected, remove all other conditions
      if (condition === "none") {
        newConditions = ["none"];
      } else {
        // If any other condition is selected, remove "none"
        newConditions = newConditions.filter((c) => c !== "none");
      }

      handleInputChange("currentMedicalConditions", newConditions);
    } else {
      handleInputChange(
        "currentMedicalConditions",
        currentConditions.filter((c) => c !== condition)
      );
      if (condition === "other") {
        handleInputChange("otherCondition", "");
      }
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    const errorOrder = ['procedures', 'dateOfBirth', 'height', 'weight'];

    
    const proceduresValue = formData.procedures;

    if (
      !proceduresValue ||
      (typeof proceduresValue === "string" && proceduresValue.trim() === "")
    ) {
      newErrors.procedures = i18n._(`Please select a procedure`);
    }
    
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = i18n._(`Date of birth is required`)  ;
    }
    if (!formData.heightFeet || !formData.heightInches) {
      newErrors.height = i18n._(`Height is required`) ;
    }
    if (!formData.weight) {
      newErrors.weight = i18n._(`Weight is required`) ;
    } 

    if ((typeof proceduresValue === "string" && proceduresValue.trim() === "dentistry") && !formData.dentalProcedureType) {
      newErrors.dentalProcedureType = i18n._(`Please select dental procedure.`) ;
    }

    setErrors(newErrors);

    // Find first error field and scroll to it
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = errorOrder.find(field => newErrors[field]);
      if (firstErrorField) {
        setTimeout(() => {
          scrollToFirstError(firstErrorField);
        }, 100);
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const scrollToFirstError = (fieldName: string) => {
    if (fieldName === 'procedures') {
      const proceduresError = document.getElementById('procedures-error');
      if (proceduresError) {
        proceduresError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        const specialtySelect = document.querySelector('[aria-invalid="true"]');
        if (specialtySelect) {
          specialtySelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    } else if (fieldName === 'height') {
      const heightLabel = document.querySelector('label[for="height"]');
      if (heightLabel) {
        heightLabel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  
    else {
      const inputElement = document.getElementById(fieldName);
      if (inputElement) {
        inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        inputElement.focus();
      }
    }
  };

  const handleContinue = async () => {
    if (isSubmitting) return;
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const allergiesMedicationsNote = formData.allergiesMedications?.trim() ?? "";
      const formattedPreExisting = formatPreExistingConditionSummary(
        formData.currentMedicalConditions,
        formData.otherCondition,
        conditionLabels,
      );
      onUpdate({
        ...formData,
        preExistingConditions: formattedPreExisting,
        currentMedications: allergiesMedicationsNote,
        allergies: allergiesMedicationsNote,
        majorSurgeries: formData.pastSurgeries?.trim() ?? "",
        alcoholConsumption: formData.alcoholUse,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => {
        onNext();
      }, 100);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-3 sm:space-y-6 px-2 sm:px-4 lg:px-0">
      {/* Header */}
      <div className="text-center space-y-2 sm:space-y-3 py-2 sm:py-4">


        <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#166B93] to-[#CEF12F] rounded-2xl mb-2 sm:mb-4">
          <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
        </div>
        <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 font-nunito px-2 sm:px-4">
          <Trans>Medical Information &amp; Travel Details</Trans>
        </h1>
        <p className="text-xs sm:text-base text-gray-600 font-jakarta max-w-2xl mx-auto leading-relaxed px-2 sm:px-4">
          <Trans>
            Help us understand your medical needs, travel plans, and health
            background for personalized care.
          </Trans>

        </p>


      </div>

      {/* Main Form */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 p-2 sm:p-6 lg:p-8">
        <div className="space-y-3 sm:space-y-6 lg:space-y-8">
          {/* 1. Procedure Selection */}
          <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-6 border border-gray-200">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-6">
              <div className="w-8 h-8 bg-[#166B93] rounded-full flex items-center justify-center text-white font-bold">
                1
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 font-nunito">

                  <Trans>Procedure of Interest</Trans>
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  <Trans>Select the medical procedure you're interested in</Trans>
                </p>
              </div>
            </div>

            <div className="mb-4">
              <Label className="text-sm sm:text-base font-medium text-gray-700 mb-4 block">
                <Trans>What procedure are you interested in? *</Trans>
              </Label>
            </div>

            <SpecialtySelect
              value={typeof formData.procedures === 'string' ? formData.procedures : ''}
              onChange={(slug: string) => {
                handleInputChange("procedures", slug);
              }}
              aria-invalid={!!errors.procedures}
              aria-describedby={errors.procedures ? "procedures-error" : undefined}>
            </SpecialtySelect>

            
            {errors.procedures && (
              <p id="procedures-error" className="text-red-500 text-sm mt-2">{errors.procedures}</p>
            )}
          </div>



          {/* 2. Health Background */}
          <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-6 border border-gray-200">

            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-6">
              <div className="w-8 h-8 bg-[#FF9142] rounded-full flex items-center justify-center text-white font-bold">
                2
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 font-nunito">

                  <Trans>Health Background</Trans>
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  <Trans>Your medical history helps us provide better care</Trans>
                </p>
              </div>
            </div>

            <div className="space-y-3 sm:space-y-6">
              {/* Basic Health Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">

                <div className="w-full">
                  <Label
                    htmlFor="height"
                    className="text-sm sm:text-base font-medium text-gray-700 mb-2 block">
                    <Trans>Height *</Trans>
                  </Label>
 
                  {/* XXXXX */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-3 w-full">
                    <div className="w-full sm:flex-1">
                      <Select
                        value={formData.heightFeet}
                        onValueChange={(value) =>
                          handleInputChange("heightFeet", value)
                        }
                      >
                        <SelectTrigger
                          className={`h-12 text-base border-2 border-gray-200 focus:border-[#166B93] transition-colors rounded-lg w-full ${errors.height ? "border-red-500" : ""}`}
                        >
                          <SelectValue placeholder={i18n._(`Feet`)}  />
                          
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200 shadow-lg rounded-xl">
                          {Array.from({ length: 6 }, (_, i) => i + 3).map((feet) => (
                            <SelectItem
                              key={feet}
                              value={feet.toString()}
                              className="hover:bg-[#166B93]/10 focus:bg-[#166B93]/10 rounded-lg"
                            >
                              {feet} ft
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="w-full sm:flex-1">
                      <Select
                        value={formData.heightInches}
                        onValueChange={(value) =>
                          handleInputChange("heightInches", value)
                        }
                      >
                        <SelectTrigger
                          className={`h-12 text-base border-2 border-gray-200 focus:border-[#166B93] transition-colors rounded-lg w-full ${errors.height ? "border-red-500" : ""}`}
                        >
                          <SelectValue placeholder={i18n._(`Inches`)}/>
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200 shadow-lg rounded-xl">
                          {Array.from({ length: 12 }, (_, i) => i).map((inches) => (
                            <SelectItem
                              key={inches}
                              value={inches.toString()}
                              className="hover:bg-[#166B93]/10 focus:bg-[#166B93]/10 rounded-lg"
                            >
                              {inches} in
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {/* XXXXX */}
                  {heightInCm && (
                    <div className="mt-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm font-medium text-gray-700">
                        <span className="text-gray-500">≈</span> {heightInCm} cm
                      </p>
                    </div>
                  )}
                  {errors.height && (
                    <p className="text-red-500 text-sm mt-1">{errors.height}</p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="weight"
                    className="text-sm sm:text-base font-medium text-gray-700 mb-2 block"
                  >

                    <Trans>Weight *</Trans>

                  </Label>
                  <div className="flex items-center gap-2 b-4">
                    <Input
                      id="weight"
                      type="number"
                      step="0.1"
                      value={formData.weight}
                      onChange={(e) =>
                        handleInputChange("weight", e.target.value)
                      }
                      className={`h-12 text-base ${errors.weight ? "border-red-500" : ""}`}
                      placeholder={i18n._(`e.g., 150`)}
                    />
                    <span className="text-base font-medium text-gray-700 whitespace-nowrap">lbs</span>
                  </div>
                  {weightInKg && (
                    <div className="mt-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm font-medium text-gray-700">
                        <span className="text-gray-500">≈</span> {weightInKg} kg
                      </p>
                    </div>
                  )}
                  {errors.weight && (
                    <p className="text-red-500 text-sm mt-1">{errors.weight}</p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="dateOfBirth"
                    className="text-sm sm:text-base font-medium text-gray-700 mb-2 block"
                  >

                    <Trans>Date of Birth *</Trans>

                  </Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) =>
                      handleInputChange("dateOfBirth", e.target.value)
                    }
                    className={`h-12 text-base ${errors.dateOfBirth ? "border-red-500" : ""}`}
                  />
                  {errors.dateOfBirth && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.dateOfBirth}
                    </p>
                  )}
                </div>


              </div>

              {/* Current Medical Conditions */}
              <div>
                <Label className="text-sm sm:text-base font-medium text-gray-700 mb-3 block">

                  <Trans>Current Medical Conditions (Select all that apply)</Trans>

                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                  {translatedConditionOptions.map((condition) => (
                    <div
                      key={condition.value}
                      className="flex items-center space-x-3"
                    >
                      <Checkbox
                        id={`condition-${condition.value}`}
                        checked={
                          Array.isArray(formData.currentMedicalConditions) &&
                          formData.currentMedicalConditions.includes(
                            condition.value
                          )
                        }
                        onCheckedChange={(checked) =>
                          handleConditionChange(condition.value, !!checked)
                        }
                        className="w-5 h-5 border-2 border-gray-300 data-[state=checked]:bg-[#166B93] data-[state=checked]:border-[#166B93]"
                      />
                      <Label
                        htmlFor={`condition-${condition.value}`}
                        className="text-sm sm:text-base text-gray-700 cursor-pointer"
                      >

                        {condition.translatedLabel}

                      </Label>
                    </div>
                  ))}
                </div>

                {/* Show "Other" input field when "Other" is selected */}
                {Array.isArray(formData.currentMedicalConditions) &&
                  formData.currentMedicalConditions.includes("other") && (
                    <div className="mt-4">
                      <Label
                        htmlFor="otherCondition"
                        className="text-sm sm:text-base font-medium text-gray-700 mb-2 block"
                      >

                        <Trans>Please specify other condition</Trans>

                      </Label>
                      <Input
                        id="otherCondition"
                        type="text"
                        value={formData.otherCondition}
                        onChange={(e) =>
                          handleInputChange("otherCondition", e.target.value)
                        }
                        className="h-12 text-base"

                        placeholder={i18n._(`Please specify your other medical condition`)}  

                      />
                    </div>
                  )}
              </div>

              {/* Past Surgeries */}
              <div>
                <Label
                  htmlFor="pastSurgeries"
                  className="text-sm sm:text-base font-medium text-gray-700 mb-2 block"
                >

                  <Trans>Past Surgeries</Trans>

                </Label>
                <Textarea
                  id="pastSurgeries"
                  value={formData.pastSurgeries}
                  onChange={(e) =>
                    handleInputChange("pastSurgeries", e.target.value)
                  }
                  className="min-h-[80px] text-base"
                  placeholder={i18n._(`List any previous surgeries with approximate dates`)}   
                />
              </div>

              {/* Allergies / Medications */}
              <div>
                <Label
                  htmlFor="allergiesMedications"
                  className="text-sm sm:text-base font-medium text-gray-700 mb-2 block"
                >

                  <Trans>Allergies / Medications</Trans>

                </Label>
                <Textarea
                  id="allergiesMedications"
                  value={formData.allergiesMedications}
                  onChange={(e) =>
                    handleInputChange("allergiesMedications", e.target.value)
                  }
                  className="min-h-[80px] text-base"
                  placeholder={i18n._(`List any previous surgeries with approximate dates`)}
                />
              </div>
            </div>
          </div>

          {/* 3. Lifestyle */}
          <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-6 border border-gray-200">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-6">
              <div className="w-8 h-8 bg-[#CEF12F] rounded-full flex items-center justify-center text-gray-900 font-bold">
                3
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 font-nunito">

                  <Trans>Lifestyle Information</Trans>
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  <Trans>Understanding your lifestyle helps with treatment planning</Trans>

                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
              {/* Smoking */}
              <div>
                <Label
                  htmlFor="smokingHabits"
                  className="text-sm sm:text-base font-medium text-gray-700 mb-2 block"
                >

                  <Trans>Smoking Habits</Trans>

                </Label>
                <Select
                  value={formData.smokingHabits}
                  onValueChange={(value) =>
                    handleInputChange("smokingHabits", value)
                  }
                >
                  <SelectTrigger className="h-12 text-base border-2 border-gray-200 focus:border-[#166B93] transition-colors rounded-lg">

                    <SelectValue placeholder= {i18n._(`Select smoking habits`)}    />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 shadow-lg rounded-xl">
                    {SMOKING_OPTIONS.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        className="hover:bg-[#166B93]/10 focus:bg-[#166B93]/10 rounded-lg"
                      >
                        {i18n._(option.label)}
                      </SelectItem>
                    ))}

                  </SelectContent>
                </Select>
              </div>

              {/* Alcohol Use */}
              <div>
                <Label
                  htmlFor="alcoholUse"
                  className="text-sm sm:text-base font-medium text-gray-700 mb-2 block"
                >

                  <Trans>Alcohol Use</Trans>

                </Label>
                <Select
                  value={formData.alcoholUse}
                  onValueChange={(value) =>
                    handleInputChange("alcoholUse", value)
                  }
                >
                  <SelectTrigger className="h-12 text-base border-2 border-gray-200 focus:border-[#166B93] transition-colors rounded-lg">

                    <SelectValue placeholder={i18n._(`Select smoking habits`)}  />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 shadow-lg rounded-xl">
                    {ALCOHOL_OPTIONS.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        className="hover:bg-[#166B93]/10 focus:bg-[#166B93]/10 rounded-lg"
                      >
                        {i18n._(option.label)}
                      </SelectItem>
                    ))}

                  </SelectContent>
                </Select>
              </div>

              {/* Exercise */}
              <div>
                <Label
                  htmlFor="exerciseFrequency"
                  className="text-sm sm:text-base font-medium text-gray-700 mb-2 block"
                >

                  <Trans>Exercise Frequency</Trans>

                </Label>
                <Select
                  value={formData.exerciseFrequency}
                  onValueChange={(value) =>
                    handleInputChange("exerciseFrequency", value)
                  }
                >
                  <SelectTrigger className="h-12 text-base border-2 border-gray-200 focus:border-[#166B93] transition-colors rounded-lg">

                    <SelectValue placeholder={i18n._(`Select exercise frequency`)}   />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 shadow-lg rounded-xl">
                    {EXERCISE_OPTIONS.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        className="hover:bg-[#166B93]/10 focus:bg-[#166B93]/10 rounded-lg"
                      >
                        {i18n._(option.label)}
                      </SelectItem>
                    ))}

                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>


          {/* 4. Dental Information Section */}
          {/* Dental Information Section */}


          {isDentistry && (
            <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-6 border border-gray-200">


              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-[#CEF12F] rounded-full flex items-center justify-center text-gray-900 font-bold">
                  4
                </div>
                <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 text-lg">🦷</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                <Trans>Dental Information</Trans>  
                </h3>
              </div>



              <div className="space-y-6">

                <div>
                  <Label
                    htmlFor="procedureType"
                    className="text-base font-medium text-gray-700 mb-2 block"
                  >
                    <Trans>What type of dental procedure are you seeking? *</Trans>  
                    
                  </Label>
                  <Select
                    value={formData.dentalProcedureType}
                    onValueChange={(value) =>
                      handleInputChange("dentalProcedureType", value)
                    }
                  >
                    <SelectTrigger
                      className={`h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors ${errors.dentalProcedureType ? "border-red-500" : "border-gray-200"}`}
                    >
                      <SelectValue placeholder={i18n._(`Select procedure type`)} /> 

                    </SelectTrigger>
                    <SelectContent>

                      <SelectItem value="implants">
                          <Trans>Dental Implants</Trans>  
                      </SelectItem>

                      <SelectItem value="veneers">
                          <Trans>Veneers</Trans>  
                      </SelectItem>

                      <SelectItem value="crowns">
                        <Trans>Crowns</Trans>  
                      </SelectItem>

                      <SelectItem value="whitening">
                         <Trans>Teeth Whitening</Trans>  
                      </SelectItem>

                      <SelectItem value="orthodontics">
                          <Trans>Orthodontics/Braces</Trans>  
                      </SelectItem>

                      <SelectItem value="restoration">
                          <Trans>General Restoration</Trans> 
                      </SelectItem>
                      
                      <SelectItem value="cosmetic">
                          <Trans>Cosmetic Dentistry</Trans> 
                      </SelectItem>

                      <SelectItem value="other">
                          <Trans>Other</Trans> 
                      </SelectItem>

                    </SelectContent>
                  </Select>
                  {errors.dentalProcedureType && (
                    <p className="text-red-500 text-sm mt-2">
                      {errors.dentalProcedureType}
                    </p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="discomfort"
                    className="text-base font-medium text-gray-700 mb-2 block">

                    <Trans>
                    Do you experience any kind of dental discomfort?
                    </Trans>
                    
                  </Label>
                  <Textarea
                    id="discomfort"
                    value={formData.dentalDiscomfort}
                    onChange={(e) =>
                      handleInputChange("dentalDiscomfort", e.target.value)
                    }
                    placeholder={i18n._(`Describe any pain, sensitivity, or discomfort or write 'None'`)}  
                    className="min-h-[100px] text-base rounded-xl border-2 focus:border-[#166B93] transition-colors border-gray-200"
                  />
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <div>
                    <Label
                      htmlFor="mobility"
                      className="text-base font-medium text-gray-700 mb-2 block"
                    >
                    <Trans>
                       Do you have any mobility issues?
                    </Trans>
                    </Label>
                    <Input
                      id="mobility"
                      value={formData.mobilityIssues}
                      onChange={(e) =>
                        handleInputChange("mobilityIssues", e.target.value)
                      }
                      placeholder={i18n._(`List any mobility concerns or write 'None'`)}  
                      className="h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors border-gray-200"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="dietary"
                      className="text-base font-medium text-gray-700 mb-2 block"
                    >
                      <Trans>Do you have any dietary restrictions?</Trans>
                    </Label>
                    <Input
                      id="dietary"
                      value={formData.dietaryRestrictions}
                      onChange={(e) =>
                        handleInputChange("dietaryRestrictions", e.target.value)
                      }
                      placeholder={i18n._(`List dietary restrictions or write 'None'`)}  
                      className="h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors border-gray-200"
                    />
                  </div>
                </div>
              </div>

            </div>
          )}


          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-3 sm:pt-6 border-t border-gray-200">
            <Button
              type="button"
              onClick={onBack}
              variant="outline"
              className="w-full sm:w-auto sm:min-w-[180px] h-12 px-6 text-base border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 font-medium transition-all duration-200 rounded-xl"
            >
              <Trans>Back</Trans>
            </Button>
            <Button
              type="button"
              onClick={handleContinue}
              disabled={isSubmitting}
              variant="jema"
              size="xl"
              className="w-full sm:w-auto sm:min-w-[200px]"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <Trans>Saving Information...</Trans>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Trans>Continue to Documents</Trans>
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              )}
            </Button>
          </div>


        </div>
      </div>
    </div>
  );
}
